import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { getDestinationBoardGuestHeaders } from "@/utils/guest-session.js";

const boardApiClient = axios;

const toInviteMetadata = (data = {}) => {
  const tokenId = data.tokenId || data.token_id || null;
  const revokedAt = data.revokedAt || data.revoked_at || null;
  let isActive = Boolean(tokenId && !revokedAt);
  if (data.isActive !== undefined) isActive = Boolean(data.isActive);
  if (data.is_active !== undefined) isActive = Boolean(data.is_active);

  return {
    tokenId,
    purpose: data.purpose || "destination_board_invite",
    permissionType: data.permissionType || data.permission_type || "edit",
    expiresAt: data.expiresAt || data.expires_at || null,
    revokedAt,
    isActive,
  };
};

const boardRequestConfig = (boardId, config = {}) => {
  const token = localStorage.getItem("token");
  const guestHeaders = token ? {} : getDestinationBoardGuestHeaders(boardId);
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...guestHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

export const useDestinationBoardStore = defineStore("destinationBoard", {
  state: () => ({
    boards: [],
    templates: [],
    currentBoard: null,
    inviteMetadata: null,
    chats: [],
    isLoading: false,
    error: null,
    chatError: null,
  }),

  actions: {
    async fetchBoardTemplates() {
      try {
        const response = await boardApiClient.get(
          `${API_URL}/destination-board-templates/`,
          boardRequestConfig()
        );
        this.templates = Array.isArray(response.data?.templates)
          ? response.data.templates
          : [];
        return this.templates;
      } catch (err) {
        console.error("fetchBoardTemplates error:", err);
        throw err;
      }
    },

    async copyBoardTemplate(templateKey, idempotencyKey) {
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-board-templates/${encodeURIComponent(templateKey)}/copies`,
          { idempotencyKey },
          boardRequestConfig()
        );
        return response.data;
      } catch (err) {
        console.error("copyBoardTemplate error:", err);
        throw err;
      }
    },

    // 自分が参加している検討ボード一覧を取得
    async fetchBoards() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.get(
          `${API_URL}/destination-boards/`,
          boardRequestConfig()
        );
        this.boards = response.data;
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "ボード一覧の取得に失敗しました";
        console.error("fetchBoards error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 検討ボードを新規作成
    async createBoard(boardTitle) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/`,
          {
            boardTitle,
          },
          boardRequestConfig()
        );
        await this.fetchBoards();
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "ボードの作成に失敗しました";
        console.error("createBoard error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 検討ボード詳細を取得
    async fetchBoardDetail(boardId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.get(
          `${API_URL}/destination-boards/${boardId}/`,
          boardRequestConfig(boardId, { suppressSessionRedirect: true })
        );
        
        // レスポンスを画面表示用に整形
        const { board, members, candidates, chats } = response.data;
        this.currentBoard = {
          ...board,
          members: members || [],
          candidates: candidates || [],
          chats: chats || []
        };
        
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "ボード詳細の取得に失敗しました";
        console.error("fetchBoardDetail error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // ボード情報（タイトルなど）を更新
    async updateBoard(boardId, boardTitle) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.put(
          `${API_URL}/destination-boards/${boardId}/`,
          { boardTitle },
          boardRequestConfig(boardId)
        );
        if (this.currentBoard && this.currentBoard.BOARD_ID === boardId) {
          this.currentBoard.BOARD_TITLE = boardTitle;
        }
        if (localStorage.getItem("token")) {
          await this.fetchBoards();
        } else {
          await this.fetchBoardDetail(boardId);
        }
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "ボードの更新に失敗しました";
        console.error("updateBoard error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // ボードを論理削除
    async deleteBoard(boardId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.delete(
          `${API_URL}/destination-boards/${boardId}/`,
          boardRequestConfig(boardId)
        );
        await this.fetchBoards();
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "ボードの削除に失敗しました";
        console.error("deleteBoard error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // メンバーの招待
    async inviteMember(boardId, memberData) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/members/`,
          memberData,
          boardRequestConfig(boardId)
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "メンバーの招待に失敗しました";
        console.error("inviteMember error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // メンバーの削除
    async removeMember(boardId, memberCode) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.delete(
          `${API_URL}/destination-boards/${boardId}/members/${memberCode}/`,
          boardRequestConfig(boardId)
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "メンバーの削除に失敗しました";
        console.error("removeMember error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // メンバー情報を更新
    async updateMember(boardId, memberCode, memberData) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.put(
          `${API_URL}/destination-boards/${boardId}/members/${memberCode}/`,
          memberData,
          boardRequestConfig(boardId)
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "メンバー情報の更新に失敗しました";
        console.error("updateMember error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 候補地を追加
    async createCandidate(boardId, formData) {
      this.isLoading = true;
      this.error = null;
      try {
        // 画像アップロード対応のため multipart/form-data を指定
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/candidates/`,
          formData,
          boardRequestConfig(boardId, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "候補地の追加に失敗しました";
        console.error("createCandidate error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 候補地を更新
    async updateCandidate(boardId, candidateNo, formData) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.put(
          `${API_URL}/destination-boards/${boardId}/candidates/${candidateNo}/`,
          formData,
          boardRequestConfig(boardId, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "候補地の更新に失敗しました";
        console.error("updateCandidate error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 候補地を削除
    async deleteCandidate(boardId, candidateNo) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.delete(
          `${API_URL}/destination-boards/${boardId}/candidates/${candidateNo}/`,
          boardRequestConfig(boardId)
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "候補地の削除に失敗しました";
        console.error("deleteCandidate error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 候補地へのスコア評価
    async registerScore(boardId, candidateNo, scoreVal, memberCode = null, scoreDetails = null) {
      const source = scoreDetails || {};
      const standardScoreDetails = scoreDetails ? {
        sightseeingScore: Number(source.sightseeingScore ?? source.sightseeing ?? source.tourism ?? 0) || 0,
        activityScore: Number(source.activityScore ?? source.activity ?? 0) || 0,
        foodScore: Number(source.foodScore ?? source.food ?? source.gourmet ?? 0) || 0,
        costScore: Number(source.costScore ?? source.cost ?? 0) || 0,
      } : null;
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/candidates/${candidateNo}/score/`,
          {
            scoreVal,
            ...(standardScoreDetails ? {
              ...standardScoreDetails,
              scoreDetails: standardScoreDetails,
              scores: standardScoreDetails,
            } : {}),
            ...(memberCode ? { memberCode } : {}),
          },
          boardRequestConfig(boardId)
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "スコア評価の登録に失敗しました";
        console.error("registerScore error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // スコア評価のクリア
    async deleteScore(boardId, candidateNo, memberCode = null) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.delete(
          `${API_URL}/destination-boards/${boardId}/candidates/${candidateNo}/score/`,
          boardRequestConfig(boardId, {
            data: memberCode ? { memberCode } : undefined,
          })
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "スコア評価の削除に失敗しました";
        console.error("deleteScore error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // チャットメッセージ投稿
    async createChatMessage(boardId, messageText) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/chats/`,
          { messageText },
          boardRequestConfig(boardId)
        );
        await this.fetchChatHistory(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "メッセージの送信に失敗しました";
        console.error("createChatMessage error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // チャット履歴を取得
    async fetchChatHistory(boardId) {
      this.isLoading = true;
      this.chatError = null;
      try {
        const response = await boardApiClient.get(
          `${API_URL}/destination-boards/${boardId}/chats/`,
          boardRequestConfig(boardId)
        );
        const chats = Array.isArray(response.data) ? response.data : [];
        this.chats = chats;
        return chats;
      } catch (err) {
        this.chatError = err.response?.data?.detail || "チャット履歴の取得に失敗しました";
        console.error("fetchChatHistory error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // AI詳細調査レポートを実行
    async aiResearchCandidate(boardId, candidateNo) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/candidates/${candidateNo}/ai-research/`,
          null,
          boardRequestConfig(boardId)
        );
        await this.fetchBoardDetail(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "AI調査レポートの実行に失敗しました";
        console.error("aiResearchCandidate error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 候補地決定（しおり移行）
    async finalizeBoardToShiori(boardId, candidateNo) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/candidates/${candidateNo}/finalize/`,
          null,
          boardRequestConfig(boardId)
        );
        return response.data; // 新規作成された shiori_id などを返す
      } catch (err) {
        this.error = err.response?.data?.detail || "しおり移行に失敗しました";
        console.error("finalizeBoardToShiori error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 招待リンク用トークン生成
    async generateShareToken(boardId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/share-token`,
          null,
          boardRequestConfig(boardId)
        );
        this.inviteMetadata = toInviteMetadata(response.data);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "共有トークンの生成に失敗しました";
        console.error("generateShareToken error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchShareTokenMetadata(boardId) {
      try {
        const response = await boardApiClient.get(
          `${API_URL}/destination-boards/${boardId}/share-token`,
          boardRequestConfig(boardId),
        );
        this.inviteMetadata = toInviteMetadata(response.data);
        return this.inviteMetadata;
      } catch (err) {
        if (err.response?.status === 404) {
          this.inviteMetadata = null;
          return null;
        }
        this.error =
          err.response?.data?.detail || "招待リンク情報の取得に失敗しました";
        console.error("Destination Board invite metadata request failed", {
          event: "destination_board_invite_metadata_failed",
          status: err.response?.status,
          code: err.code,
        });
        throw err;
      }
    },

    async rotateShareToken(boardId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/${boardId}/share-token/rotate`,
          null,
          boardRequestConfig(boardId),
        );
        this.inviteMetadata = toInviteMetadata(response.data);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "招待リンクの再発行に失敗しました";
        console.error("Destination Board invite rotation failed", {
          event: "destination_board_invite_rotation_failed",
          status: err.response?.status,
          code: err.code,
        });
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async revokeShareToken(boardId, tokenId) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.delete(
          `${API_URL}/destination-boards/${boardId}/share-token/${tokenId}`,
          boardRequestConfig(boardId),
        );
        await this.fetchShareTokenMetadata(boardId);
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "招待リンクの無効化に失敗しました";
        console.error("Destination Board invite revocation failed", {
          event: "destination_board_invite_revocation_failed",
          status: err.response?.status,
          code: err.code,
        });
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // AIに都市候補を提案させる
    async proposeAiDestinations(params) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/ai-trips/destination-proposals/`,
          params,
          boardRequestConfig(undefined, { timeout: 120000 })
        );
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "AI都市提案に失敗しました";
        console.error("proposeAiDestinations error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // AI提案からボードを自動生成
    async createBoardFromAiProposal(params) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await boardApiClient.post(
          `${API_URL}/destination-boards/create-from-ai-proposal/`,
          params,
          boardRequestConfig()
        );
        await this.fetchBoards();
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.detail || "AI提案からのボード自動生成に失敗しました";
        console.error("createBoardFromAiProposal error:", err);
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // 候補地画像 blob を取得
    async fetchCandidateImage(boardId, candidateNo, { signal, timeout } = {}) {
      this.error = null;
      try {
        const response = await boardApiClient.get(
          `${API_URL}/destination-boards/${boardId}/candidates/${candidateNo}/image`,
          boardRequestConfig(boardId, {
            responseType: "blob",
            ...(signal ? { signal } : {}),
            ...(timeout ? { timeout } : {}),
          })
        );
        return response.data;
      } catch (err) {
        console.error("fetchCandidateImage error:", err);
        throw err;
      }
    },
  },
});
